import React, { useState, useEffect } from 'react';
import {
    Card, Form, Input, Button, message, Alert, Select,
    Space, Typography, Divider, Row, Col, Tag, Modal,
    Tooltip, Empty, Badge, Popconfirm
} from 'antd';
import {
    ApiOutlined, SaveOutlined, PlusOutlined, DeleteOutlined,
    CheckCircleOutlined, EditOutlined, KeyOutlined,
    RocketOutlined, QuestionCircleOutlined, LinkOutlined
} from '@ant-design/icons';
import api from '../../services/api';

const { Title, Text, Paragraph } = Typography;

// 预设的热门 AI 供应商配置
const popularProviders = [
    {
        name: 'mirrorstages',
        displayName: 'MirrorStages (GPT-5.3)',
        icon: '🪞',
        description: 'GPT-5.3 Codex 高级推理模型，支持高质量代码生成',
        apiBase: 'https://api.mirrorstages.com/openai/v1',
        models: ['gpt-5.3-codex', 'gpt-5.3-codex-spark', 'gpt-5.2-codex', 'gpt-5.1-codex', 'gpt-5-codex', 'gpt-4', 'gpt-4-turbo', 'gpt-3.5-turbo'],
        getKeyUrl: 'https://api.mirrorstages.com',
        features: ['高级推理', '代码生成', 'OpenAI 兼容'],
        pricing: '按使用量计费'
    },
    {
        name: 'zhipu',
        displayName: '智谱AI (GLM)',
        icon: '🤖',
        description: '国产大模型，支持长文本和多模态',
        apiBase: 'https://open.bigmodel.cn/api/paas/v4',
        models: ['glm-5', 'glm-4.7', 'glm-4.5-air', 'glm-4-plus', 'glm-4-0520', 'glm-4-air', 'glm-4-airx', 'glm-4-flash', 'glm-4v', 'glm-z1-airx'],
        getKeyUrl: 'https://open.bigmodel.cn/usercenter/apikeys',
        features: ['长文本', '多模态', '函数调用'],
        pricing: '约 ¥0.005/千token'
    },
    {
        name: 'deepseek',
        displayName: 'DeepSeek',
        icon: '🔍',
        description: '高性价比的国产大模型',
        apiBase: 'https://api.deepseek.com/v1',
        models: ['deepseek-chat', 'deepseek-reasoner'],
        getKeyUrl: 'https://platform.deepseek.com/api_keys',
        features: ['高性价比', '代码能力强', '快速响应'],
        pricing: '约 ¥0.001/千token'
    },
    {
        name: 'qwen',
        displayName: '通义千问 (Qwen)',
        icon: '💬',
        description: '阿里云大模型，稳定可靠',
        apiBase: 'https://dashscope.aliyuncs.com/compatible-mode/v1',
        models: ['qwen3-max', 'qwen-plus', 'qwen-max', 'qwen-turbo', 'qwen-vl-max'],
        getKeyUrl: 'https://dashscope.console.aliyun.com/apiKey',
        features: ['企业级', '多模态', '低延迟'],
        pricing: '约 ¥0.008/千token'
    },
    {
        name: 'openai',
        displayName: 'OpenAI (官方)',
        icon: '🌐',
        description: 'GPT系列模型，全球领先',
        apiBase: 'https://api.openai.com/v1',
        models: ['gpt-5', 'gpt-5-mini', 'gpt-4.1', 'gpt-4.1-mini', 'gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo', 'gpt-3.5-turbo', 'o3', 'o4-mini'],
        getKeyUrl: 'https://platform.openai.com/api-keys',
        features: ['最强能力', '多模态', '插件生态'],
        pricing: '较高，需海外支付'
    },
    {
        name: 'minimax',
        displayName: 'MiniMax',
        icon: '⚡',
        description: '国产新锐大模型',
        apiBase: 'https://api.minimax.chat/v1',
        models: ['MiniMax-M2.5', 'MiniMax-M2.1', 'minimax-m2.5', 'abab6.5s-chat', 'abab6-chat', 'abab5.5-chat'],
        getKeyUrl: 'https://www.minimax.chat/',
        features: ['中文优化', '快速推理', '长文本'],
        pricing: '约 ¥0.006/千token'
    },
    {
        name: 'volcengine',
        displayName: '字节豆包 (Volcengine)',
        icon: '🎯',
        description: '字节跳动旗下大模型',
        apiBase: 'https://ark.cn-beijing.volces.com/api/v3',
        models: ['doubao-pro-32k', 'doubao-lite-32k'],
        getKeyUrl: 'https://console.volcengine.com/ark',
        features: ['多模态', '高并发', '低延迟'],
        pricing: '约 ¥0.004/千token'
    },
    {
        name: 'moonshot',
        displayName: 'Moonshot (月之暗面)',
        icon: '🌙',
        description: '支持超长上下文',
        apiBase: 'https://api.moonshot.cn/v1',
        models: ['moonshot-v1-8k', 'moonshot-v1-32k', 'moonshot-v1-128k'],
        getKeyUrl: 'https://platform.moonshot.cn/console/api-keys',
        features: ['超长文本', '记忆能力强', 'RAG优化'],
        pricing: '按文本长度计费'
    },
    {
        name: 'custom',
        displayName: '自定义供应商',
        icon: '⚙️',
        description: '配置任意兼容 OpenAI API 的服务',
        apiBase: '',
        models: [],
        getKeyUrl: '',
        features: ['完全自定义', '兼容性强'],
        pricing: '根据您的供应商而定'
    }
];

const PersonalAISettings = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [configs, setConfigs] = useState([]); // 所有已配置的供应商列表
    const [modalVisible, setModalVisible] = useState(false);
    const [editingProvider, setEditingProvider] = useState(null); // 正在编辑的供应商名
    const [selectedPreset, setSelectedPreset] = useState(null);

    useEffect(() => {
        fetchConfigs();
    }, []);

    const fetchConfigs = async () => {
        setLoading(true);
        try {
            const res = await api.get('/user-ai-auth/me');
            if (res.data.success) {
                setConfigs(res.data.data || []);
            }
        } catch (error) {
            console.error('获取个人AI配置失败:', error);
        } finally {
            setLoading(false);
        }
    };

    // 打开添加/编辑弹窗
    const openAddModal = (existingConfig = null) => {
        form.resetFields();
        setSelectedPreset(null);
        if (existingConfig) {
            setEditingProvider(existingConfig.provider_name);
            const isCustom = existingConfig.provider_name === 'custom' || existingConfig.provider_name.startsWith('custom_');
            const presetName = isCustom ? 'custom' : existingConfig.provider_name;
            const preset = popularProviders.find(p => p.name === presetName);
            setSelectedPreset(preset);

            // 如果是自定义供应商，尝试将 models 数组转回易读的 JSON 结构
            let customModelsStr = '';
            if (isCustom && existingConfig.models && existingConfig.models.length > 0) {
                customModelsStr = JSON.stringify({
                    "env": {
                        "CUSTOM_MODELS": existingConfig.models.join(',')
                    }
                }, null, 2);
            }

            form.setFieldsValue({
                provider_name: presetName,
                custom_name: existingConfig.custom_name || '',
                api_key: existingConfig.api_key || '',
                api_base: existingConfig.api_base || preset?.apiBase || '',
                custom_models_json: customModelsStr
            });
        } else {
            setEditingProvider(null);
        }
        setModalVisible(true);
    };

    const handleProviderSelect = (providerName) => {
        const preset = popularProviders.find(p => p.name === providerName);
        setSelectedPreset(preset);
        if (preset?.apiBase) {
            form.setFieldsValue({ api_base: preset.apiBase, custom_models_json: '' });
        } else {
            form.setFieldsValue({ api_base: '', custom_models_json: '' });
        }
    };

    const handleSave = async (values) => {
        setSaving(true);
        try {
            const preset = popularProviders.find(p => p.name === values.provider_name);
            let finalModels = preset?.models || [];
            let theProviderName = values.provider_name;
            const isCustom = values.provider_name === 'custom';

            // 如果是自定义供应商，尝试解析 JSON 配置提取模型
            if (isCustom && values.custom_models_json) {
                try {
                    // 支持简单的逗号分隔列表
                    if (!values.custom_models_json.trim().startsWith('{')) {
                        finalModels = values.custom_models_json.split(',').map(s => s.trim()).filter(Boolean);
                    } else {
                        // 解析 JSON
                        const parsedObj = JSON.parse(values.custom_models_json);
                        const collectedModels = new Set();

                        // 递归查找所有的字符串值，如果看起来像模型名字就收集
                        const extractModels = (obj) => {
                            if (!obj) return;
                            if (typeof obj === 'string') {
                                // 简单启发式：如果包含特定关键词或者是很像是模型名
                                if (obj.includes('gpt') || obj.includes('claude') || obj.includes('opus') ||
                                    obj.includes('sonnet') || obj.includes('haiku') || obj.includes('codex')) {
                                    collectedModels.add(obj);
                                }
                            } else if (Array.isArray(obj)) {
                                obj.forEach(extractModels);
                            } else if (typeof obj === 'object') {
                                Object.values(obj).forEach(extractModels);
                                // 如果有特定的逗号分隔的 models 列表
                                if (obj.CUSTOM_MODELS && typeof obj.CUSTOM_MODELS === 'string') {
                                    obj.CUSTOM_MODELS.split(',').forEach(s => collectedModels.add(s.trim()));
                                }
                            }
                        };

                        extractModels(parsedObj);
                        if (collectedModels.size > 0) {
                            finalModels = Array.from(collectedModels);
                        } else {
                            // 如果实在没有匹配到，就把所有叶子节点的值当做模型试试 (作为最后的后备)
                            Object.values(parsedObj?.env || {}).forEach(val => {
                                if (typeof val === 'string' && val.length < 50 && !val.includes('http') && val !== 'default') {
                                    collectedModels.add(val);
                                }
                            });
                            finalModels = Array.from(collectedModels);
                        }
                    }
                } catch (e) {
                    message.error('自定义支持的模型 JSON 格式不正确');
                    setSaving(false);
                    return;
                }
            }

            if (isCustom) {
                if (editingProvider && (editingProvider === 'custom' || editingProvider.startsWith('custom_'))) {
                    theProviderName = editingProvider;
                } else if (!editingProvider) {
                    theProviderName = `custom_${Date.now()}`;
                }
            }

            const payload = {
                provider_name: theProviderName,
                custom_name: values.custom_name || '',
                api_key: values.api_key || '',
                api_base: values.api_base || preset?.apiBase || '',
                models: finalModels
            };
            await api.post('/user-ai-auth/me', payload);
            message.success('保存成功！重启桌面客户端即可使用新配置。');
            setModalVisible(false);
            fetchConfigs();
        } catch (error) {
            message.error(error.response?.data?.message || '保存失败');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (providerName) => {
        try {
            await api.delete(`/user-ai-auth/me/${providerName}`);
            message.success('已删除该供应商配置');
            fetchConfigs();
        } catch (error) {
            message.error('删除失败');
        }
    };

    // 根据 provider_name 查找预设信息
    const getPresetInfo = (providerName) => {
        return popularProviders.find(p => p.name === providerName) || {
            displayName: providerName,
            icon: '⚙️',
            features: [],
            pricing: ''
        };
    };

    return (
        <div style={{ padding: '24px', maxWidth: 1000, margin: '0 auto' }}>
            {/* 页面标题 */}
            <div style={{ marginBottom: 24 }}>
                <Title level={3} style={{ margin: 0 }}>
                    🔑 个人专属 AI 配置
                </Title>
                <Text type="secondary">
                    配置您自己的 AI 供应商 API Key，享受独立额度和更快速度。可以同时配置多个供应商。
                </Text>
            </div>

            {/* 已配置的供应商列表 */}
            <Card
                title={
                    <Space>
                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                        <span>已配置的供应商</span>
                        <Badge count={configs.length} style={{ backgroundColor: '#1890ff' }} />
                    </Space>
                }
                extra={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => openAddModal()}
                    >
                        添加供应商
                    </Button>
                }
                style={{ marginBottom: 24 }}
                loading={loading}
            >
                {configs.length === 0 ? (
                    <Empty
                        description={
                            <span>
                                暂无个人配置，点击上方"添加供应商"开始配置
                                <br />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    未配置时将使用学校公共 AI 路线
                                </Text>
                            </span>
                        }
                        style={{ padding: '40px 0' }}
                    />
                ) : (
                    <Row gutter={[16, 16]}>
                        {configs.map(config => {
                            const isCustom = config.provider_name === 'custom' || config.provider_name.startsWith('custom_');
                            const presetName = isCustom ? 'custom' : config.provider_name;
                            const preset = getPresetInfo(presetName);
                            const displayName = isCustom && config.custom_name ? config.custom_name : preset.displayName;
                            return (
                                <Col key={config.provider_name} xs={24} sm={12} lg={8}>
                                    <Card
                                        size="small"
                                        hoverable
                                        style={{ borderColor: '#52c41a', borderWidth: 1.5 }}
                                        actions={[
                                            <Tooltip title="编辑配置" key="edit">
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<EditOutlined />}
                                                    onClick={() => openAddModal(config)}
                                                >
                                                    编辑
                                                </Button>
                                            </Tooltip>,
                                            <Popconfirm
                                                key="delete"
                                                title="确认删除此供应商配置？"
                                                description="删除后该供应商将使用学校公共 Key"
                                                onConfirm={() => handleDelete(config.provider_name)}
                                                okText="删除"
                                                okType="danger"
                                                cancelText="取消"
                                            >
                                                <Button type="text" size="small" danger icon={<DeleteOutlined />}>
                                                    删除
                                                </Button>
                                            </Popconfirm>
                                        ]}
                                    >
                                        <div style={{ marginBottom: 8 }}>
                                            <Space>
                                                <span style={{ fontSize: 20 }}>{preset.icon}</span>
                                                <div>
                                                    <div style={{ fontWeight: 600, fontSize: 13 }}>
                                                        {displayName}
                                                    </div>
                                                    <div>
                                                        <Badge status="success" text={<Text style={{ fontSize: 11 }}>已配置</Text>} />
                                                    </div>
                                                </div>
                                            </Space>
                                        </div>
                                        <div style={{ marginBottom: 6 }}>
                                            <KeyOutlined style={{ color: '#8c8c8c', marginRight: 4 }} />
                                            <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>
                                                {config.api_key_masked}
                                            </Text>
                                        </div>
                                        {config.models && config.models.length > 0 && (
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                                                {config.models.slice(0, 3).map(m => (
                                                    <Tag key={m} style={{ fontSize: 10, margin: 0, padding: '0 4px' }}>
                                                        {m}
                                                    </Tag>
                                                ))}
                                                {config.models.length > 3 && (
                                                    <Tag style={{ fontSize: 10, margin: 0, padding: '0 4px' }}>
                                                        +{config.models.length - 3}
                                                    </Tag>
                                                )}
                                            </div>
                                        )}
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                )}
            </Card>

            {/* 说明卡片 */}
            <Card size="small" style={{ backgroundColor: '#f6ffed', borderColor: '#b7eb8f' }}>
                <Row gutter={24}>
                    <Col span={8}>
                        <Space direction="vertical" size={4}>
                            <Text strong>🚀 独立额度</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                使用您自己的 API Key，不与他人共享配额，不受学校限速影响
                            </Text>
                        </Space>
                    </Col>
                    <Col span={8}>
                        <Space direction="vertical" size={4}>
                            <Text strong>🔀 多供应商切换</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                可同时配置多个供应商，在桌面客户端下拉框中自由切换
                            </Text>
                        </Space>
                    </Col>
                    <Col span={8}>
                        <Space direction="vertical" size={4}>
                            <Text strong>🛡️ 优先使用</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                个人配置优先级高于学校配置，同一供应商会自动使用您的 Key
                            </Text>
                        </Space>
                    </Col>
                </Row>
            </Card>

            {/* 添加/编辑弹窗 */}
            <Modal
                title={
                    <Space>
                        <ApiOutlined />
                        {editingProvider ? '编辑供应商配置' : '添加 AI 供应商'}
                    </Space>
                }
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={600}
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSave}
                    style={{ marginTop: 16 }}
                >
                    {/* 供应商选择 */}
                    <Form.Item
                        label="选择 AI 供应商"
                        name="provider_name"
                        rules={[{ required: true, message: '请选择供应商' }]}
                    >
                        <Select
                            placeholder="从推荐列表中选择..."
                            size="large"
                            showSearch
                            optionFilterProp="label"
                            onChange={handleProviderSelect}
                            disabled={!!editingProvider}
                        >
                            {popularProviders.map(p => (
                                <Select.Option key={p.name} value={p.name} label={p.displayName}>
                                    <Space>
                                        <span>{p.icon}</span>
                                        <span>{p.displayName}</span>
                                        <Text type="secondary" style={{ fontSize: 11 }}>{p.pricing}</Text>
                                    </Space>
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) => prevValues.provider_name !== currentValues.provider_name}
                    >
                        {({ getFieldValue }) =>
                            getFieldValue('provider_name') === 'custom' ? (
                                <Form.Item
                                    label="自定义名称"
                                    name="custom_name"
                                    rules={[{ required: true, message: '请输入自定义名称' }]}
                                >
                                    <Input size="large" placeholder="例如：我的 Ollama 接口、张三的代理" />
                                </Form.Item>
                            ) : null
                        }
                    </Form.Item>

                    {/* 供应商详情提示 */}
                    {selectedPreset && (
                        <Alert
                            style={{ marginBottom: 16 }}
                            message={
                                <Space>
                                    <span>{selectedPreset.icon}</span>
                                    <strong>{selectedPreset.displayName}</strong>
                                </Space>
                            }
                            description={
                                <div>
                                    <div style={{ marginBottom: 6 }}>{selectedPreset.description}</div>
                                    <Space wrap style={{ marginBottom: 6 }}>
                                        {selectedPreset.features.map(f => (
                                            <Tag key={f} color="blue" style={{ fontSize: 11 }}>{f}</Tag>
                                        ))}
                                    </Space>
                                    {selectedPreset.models.length > 0 && (
                                        <div style={{ marginBottom: 6 }}>
                                            <Text type="secondary" style={{ fontSize: 11 }}>可用模型：</Text>
                                            <Space wrap>
                                                {selectedPreset.models.map(m => (
                                                    <Tag key={m} style={{ fontSize: 10, margin: '2px 2px' }}>{m}</Tag>
                                                ))}
                                            </Space>
                                        </div>
                                    )}
                                    {selectedPreset.getKeyUrl && (
                                        <a href={selectedPreset.getKeyUrl} target="_blank" rel="noreferrer">
                                            <LinkOutlined /> 获取 API Key →
                                        </a>
                                    )}
                                </div>
                            }
                            type="info"
                            showIcon={false}
                        />
                    )}

                    {/* API Key */}
                    <Form.Item
                        label={
                            <Space>
                                <span>API Key</span>
                                <Tooltip title="您的个人 API Key，由供应商平台申请">
                                    <QuestionCircleOutlined style={{ color: '#8c8c8c' }} />
                                </Tooltip>
                            </Space>
                        }
                        name="api_key"
                        rules={[{ required: true, message: '请输入 API Key' }]}
                    >
                        <Input.Password
                            size="large"
                            placeholder="sk-... 或 api:..."
                            prefix={<KeyOutlined style={{ color: '#bfbfbf' }} />}
                        />
                    </Form.Item>

                    {/* API Base URL */}
                    <Form.Item
                        label={
                            <Space>
                                <span>API Base URL（可选）</span>
                                <Tooltip title="通常会自动填充，无需修改。使用代理或自定义端点时才需要更改。">
                                    <QuestionCircleOutlined style={{ color: '#8c8c8c' }} />
                                </Tooltip>
                            </Space>
                        }
                        name="api_base"
                    >
                        <Input
                            size="large"
                            placeholder="https://api.example.com/v1"
                            prefix={<LinkOutlined style={{ color: '#bfbfbf' }} />}
                        />
                    </Form.Item>

                    {/* 自定义模型配置 JSON (仅在选择自定义供应商时显示) */}
                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) => prevValues.provider_name !== currentValues.provider_name}
                    >
                        {({ getFieldValue }) =>
                            getFieldValue('provider_name') === 'custom' ? (
                                <Form.Item
                                    label={
                                        <Space>
                                            <span>支持的模型 (JSON配置)</span>
                                            <Tooltip title="粘贴第三方平台的 JSON 配置代码，系统将自动解析模型。或直接填写以逗号分隔的模型列表。">
                                                <QuestionCircleOutlined style={{ color: '#8c8c8c' }} />
                                            </Tooltip>
                                        </Space>
                                    }
                                    name="custom_models_json"
                                >
                                    <Input.TextArea
                                        rows={6}
                                        placeholder={`例如可以直接粘贴：\n{\n  "env": {\n    "ANTHROPIC_MODEL": "gpt-5.3-codex"\n  }\n}\n或直接输入：gpt-3.5-turbo, claude-3-opus`}
                                    />
                                </Form.Item>
                            ) : null
                        }
                    </Form.Item>

                    <Divider style={{ margin: '12px 0' }} />

                    <Form.Item style={{ marginBottom: 0 }}>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button onClick={() => setModalVisible(false)}>取消</Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={saving}
                                icon={<SaveOutlined />}
                                size="large"
                            >
                                保存配置
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default PersonalAISettings;
